import React, { useState, useEffect, useRef } from 'react';
import { SupplierInfo } from '../types/supplierInfo.types';
import { SaveIcon } from '@/shared/components/icons/Icons';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';
import { sanitizeAccountHolder, sanitizeAccountNumber } from '../utils/inputValidationHelpers';

interface SettlementInfoSectionProps {
    supplierInfo: SupplierInfo;
    saving: boolean;
    onFieldChange: (field: keyof SupplierInfo | Partial<SupplierInfo>, value?: string) => void;
    onSave: () => void;
}

const SettlementInfoSection: React.FC<SettlementInfoSectionProps> = ({
                                                                         supplierInfo,
                                                                         saving,
                                                                         onFieldChange,
                                                                         onSave
                                                                     }) => {
    const [bankCodes, setBankCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedBankCodes = useRef(false);

    useEffect(() => {
        if (hasFetchedBankCodes.current) return;
        let isCancelled = false;
        const fetchBankCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('BANK_CODE');
                if (!isCancelled) {
                    setBankCodes(response.codes || []);
                    hasFetchedBankCodes.current = true;
                }
            } catch {
                // 은행 코드 조회 실패 시 기본값 유지
            }
        };
        fetchBankCodes();
        return () => { isCancelled = true; };
    }, []);

    const selectedBank = bankCodes.find(bank => bank.comCode === supplierInfo.bankCode);
    const selectedBankName = selectedBank?.comCdName || supplierInfo.bankName || '';

    const handleBankChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const bank = bankCodes.find(b => b.comCdName === e.target.value);
        if (bank) {
            onFieldChange({ bankCode: bank.comCode, bankName: bank.comCdName } as Partial<SupplierInfo>);
        }
    };

    return (
        <div className="detail-card">
            <div className="detail-card-header">
                <i className="fas fa-calculator card-icon" />
                <h3 className="card-title">정산 정보</h3>
            </div>

            <div className="info-table">
                <div className="info-row">
                    <div className="info-label">은행명</div>
                    <div className="info-value">
                        <select value={selectedBankName} onChange={handleBankChange}>
                            <option value="">선택하세요</option>
                            {bankCodes.map(bank => (
                                <option key={bank.comCode} value={bank.comCdName}>
                                    {bank.comCdName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="info-label">계좌번호</div>
                    <div className="info-value">
                        <input
                            type="text"
                            value={supplierInfo.accountNumber}
                            onChange={(e) => onFieldChange('accountNumber', sanitizeAccountNumber(e.target.value))}
                        />
                    </div>
                </div>

                <div className="info-row">
                    <div className="info-label">예금주</div>
                    <div className="info-value">
                        <input
                            type="text"
                            value={supplierInfo.accountHolder}
                            onChange={(e) => onFieldChange('accountHolder', sanitizeAccountHolder(e.target.value))}
                        />
                    </div>
                    <div className="info-label"></div>
                    <div className="info-value"></div>
                </div>
            </div>

            <div className="section-actions">
                <button className="save-btn" onClick={onSave} disabled={saving}>
                    <SaveIcon width={16} height={16} />
                    <span>{saving ? '저장 중...' : '저장'}</span>
                </button>
            </div>
        </div>
    );
};

export default SettlementInfoSection;
